import { Directive, ElementRef, Input, OnDestroy, afterNextRender, inject, numberAttribute } from '@angular/core';
import { gsap } from '../gsap';
import { QUERIES } from '../motion-env';
import { MotionService } from '../motion.service';

/**
 * The element leans toward the pointer (and its `.magnetic-label` child a little less), then
 * springs back. Desktop fine pointers only; the hit area and cursor are unchanged.
 */
@Directive({ selector: '[appMagnetic]' })
export class MagneticDirective implements OnDestroy {
  @Input({ transform: numberAttribute }) magneticStrength = 0.3;
  /** Maximum offset in px. */
  @Input({ transform: numberAttribute }) magneticMax = 8;

  private readonly el: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private destroyed = false;

  constructor() {
    this.el.classList.add('dv-magnetic');
    afterNextRender(() => this.motion.afterPaint(() => this.destroyed || this.setup()));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mm?.revert();
  }

  private setup(): void {
    const el = this.el;
    const mm = (this.mm = gsap.matchMedia(el));
    mm.add({ motion: QUERIES.motion, fine: QUERIES.fine }, (ctx, contextSafe) => {
      const conditions = ctx.conditions!;
      if (!conditions['motion'] || !conditions['fine']) return;

      const label = el.querySelector<HTMLElement>('.magnetic-label');
      const xTo = gsap.quickTo(el, 'x', { duration: 0.4, ease: 'power3.out' });
      const yTo = gsap.quickTo(el, 'y', { duration: 0.4, ease: 'power3.out' });
      const lx = label ? gsap.quickTo(label, 'x', { duration: 0.4, ease: 'power3.out' }) : null;
      const ly = label ? gsap.quickTo(label, 'y', { duration: 0.4, ease: 'power3.out' }) : null;
      const clamp = (v: number) => Math.max(-this.magneticMax, Math.min(this.magneticMax, v));

      let frame = 0;
      let px = 0;
      let py = 0;
      const apply = () => {
        frame = 0;
        const r = el.getBoundingClientRect();
        const tx = clamp((px - (r.left + r.width / 2)) * this.magneticStrength);
        const ty = clamp((py - (r.top + r.height / 2)) * this.magneticStrength);
        xTo(tx);
        yTo(ty);
        lx?.(tx * 0.3);
        ly?.(ty * 0.3);
      };
      const onMove = (e: PointerEvent) => {
        px = e.clientX;
        py = e.clientY;
        if (!frame) frame = requestAnimationFrame(apply);
      };
      const onLeave = contextSafe!(() => {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        gsap.to([el, label].filter(Boolean), { x: 0, y: 0, duration: 0.8, ease: 'elastic.out(1,0.45)', overwrite: 'auto' });
      }) as () => void;

      el.addEventListener('pointermove', onMove);
      el.addEventListener('pointerleave', onLeave);
      return () => {
        el.removeEventListener('pointermove', onMove);
        el.removeEventListener('pointerleave', onLeave);
        if (frame) cancelAnimationFrame(frame);
      };
    });
  }
}
